
import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FieldPath, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import MoreIcon from '@mui/icons-material/More';
import SearchIcon from '@mui/icons-material/Search';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';
import useTeacherService, { useBasicFilterTeacher } from './services/Teacher';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IEntityNameId, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, FormHelperText, Link, ToggleButtonGroup, ToggleButton, Autocomplete, FormControl, InputLabel, Select, ListSubheader } from '@mui/material';
import NumberFormat from 'react-number-format';

import { ITeacher, ITeacherSheet, defaultTeacher } from './models/Teacher';

import usePersonService, { useBasicFilterPerson, useBasicFilterPersonSearch } from 'features/production/services/Person';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration, {
  IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE, Enum_ETHNICITY,
  Enum_DEFAULT_LANGUAGE, Enum_TOWN, Enum_COUNTRY, Enum_PERSON_IDENTIFICATION, Enum_EMPLOYEE_TYPE,
  Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_MARIETAL_STATUS,
  Enum_TEACHER_STATUS, Enum_TEACHER_DIPLOMA, Enum_TEACHER_SPECIALIZATION, Enum_TEACHER_FUNCTION, Enum_TEACHER_SERVICE,
  Enum_CHRONIC_ILLNESS, Enum_SPECIFIC_NEED_PURPOSE, Enum_DISABILITY_TYPE
} from 'features/configuration/models/Enumeration';

import { ITarification } from 'features/configuration/models/Tarification';


import { IExtensionType } from 'features/configuration/models/ExtensionType';


import { carouselImage, justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';

import useSchoolYearService, { useBasicFilterSchoolYear, useBasicFilterSchoolYearClass } from 'features/setup/services/SchoolYear';
import { ISchoolYear, ISchoolYearClass, ISchoolYearClassSubject } from 'features/setup/models/SchoolYear';
import { DatePicker } from '@mui/x-date-pickers';
import { IClassTemplate } from 'features/setup/models/ClassTemplate';
import useClassTemplateService, { useBasicFilterClassTemplate } from 'features/setup/services/ClassTemplate';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { IBilling, defaultBilling } from 'features/finance/models/Billing';
import { IPerson } from 'features/production/models/Person';

import { IBillingType } from 'features/setup/models/BillingType';
import { useBasicFilterBillingType } from 'features/setup/services/BillingType';
import ExtensionValueField from 'components/ui/ExtensionValueField';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';
import EnhancedTable from 'components/ui/EnhancedTable';
import { debounce } from 'lodash';
import { GrClose, GrSearch } from 'react-icons/gr';


export const TeacherForm: FC<ITeacher> = (props: ITeacher = defaultTeacher) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { id } = useParams();

  const { language: lg, applicationSetup, schoolYears, roleEntities } = useRecoilValue(currentUserSessionAtom);

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer,
    checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();

  const { createTeacher, updateTeacher, getTeacherSheets, getTeacherSubjects, setSubjectTeacher } = useTeacherService();

  const { getPersons, getPersonsSearchCount, getParentPortables, getParentEmails } = usePersonService();

  const { getSchoolYearClassSubjects } = useSchoolYearService();

  const { getClassTemplates } = useClassTemplateService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  //const refSchoolYears = useRef<ISchoolYear[]>();

  const [schoolYearClassSubjects, setSchoolYearClassSubjects] = useState<ISchoolYearClassSubject[]>([]);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterTeacher = useBasicFilterTeacher(
    (event: React.MouseEvent<unknown>, row: ITeacher) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const { canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  const [openPersonFilter, setOpenPersonFilter] = useState(false);
  const basicFilterPerson = useBasicFilterPerson(
    (event: React.MouseEvent<unknown>, row: IPerson) => {
      const { id, firstName, lastName, birthDate, birthPlace, particularSign } = row;

      setValue('personId', id);
      setValue('firstName', firstName);
      setValue('lastName', lastName);
      setValue('birthDate', birthDate);
      setValue('birthPlace', birthPlace);
      setValue('particularSign', particularSign);

      setOpenPersonFilter(false);
    }
  );


  const [openClassFilter, setOpenClassFilter] = useState(false);
  const basicFilterSchoolYearClass = useBasicFilterSchoolYearClass(
    async (event: React.MouseEvent<unknown>, row: ISchoolYearClass) => {

      //const schoolYearClass = await retrieveEntity('SchoolYearClass',row.id) as ISchoolYearClass;
      const { id, studyType, name, schoolYearClassTemplateId,
        classTemplateName, studyLanguage,
        fullTeacherName, substituteTeacherName,
        schoolYearId
      } = row;


      setValue('currentSchoolYearClassId', id);
      setValue('currentSchoolYearClassName', name);

      const arr = await getSchoolYearClassSubjects(id); // schoolYearClassId
      setSchoolYearClassSubjects([...arr]);

      setOpenClassFilter(false);
    }
  );


  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity(
    (event: React.MouseEvent<unknown>, row: IEntity) => {
      const { name, description } = row;

      // setValue('entityName', name || '');
      // setValue('entityDescription', description || '');

      setOpenEntityFilter(false);
    }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;

      // setValue('enumerationId', id);
      // setValue('enumerationName', name);

      setOpenEnumerationFilter(false);
    }
  );

  const emptyFunc = (obj: any) => { }

  const methods = useForm<ITeacher>({
    defaultValues: {
      ...defaultTeacher,
      currentSchoolYearId: applicationSetup.currentSchoolYearId,
      currentSchoolYearClassId: 0,
      currentSchoolYearClassSubjectId: 0
    }
  });

  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchBase64Picture = watch('base64Picture');

  const watchTeacherId = watch('id');
  const watchPersonId = watch('personId');

  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');
  const watchParticularSign = watch('particularSign');

  const watchStatus = watch('status');
  const watchLastDiploma = watch('lastDiploma');
  const watchSpecialization = watch('specialization');
  const watchServiceCode = watch('serviceCode');
  const watchFunctionCode = watch('functionCode');
  const watchEmployeeType = watch('employeeType');

  const watchFatherId = watch('fatherId');
  const watchMotherId = watch('motherId');
  const watchTutorId = watch('tutorId');

  const watchHasSpecificNeeds = watch('hasSpecificNeeds');
  const watchHasDisability = watch('hasDisability');

  const watchGender = watch('gender');

  const watchFatherGender = watch('fatherGender');
  const watchMotherGender = watch('motherGender');
  const watchTutorGender = watch('tutorGender');

  const watchCurrentSchoolYearId = watch('currentSchoolYearId');


  const [openPersonSearchFilter, setOpenPersonSearchFilter] = useState(false);
    const basicFilterPersonSearch = useBasicFilterPersonSearch(
      () => {
        const { firstName, lastName, particularSign } = getValues();
        return { firstName, lastName, particularSign };
        //return refPersonSearch.current || {firstName: '', lastName: '', particularSign: ''};
      },
      async (event: React.MouseEvent<unknown>, row: IPerson) => {
        const { id, firstName, lastName, birthDate, birthPlace, particularSign, civility, gender, 
          fatherId, motherId, tutorId,
          birthCertificateNumber, civilRegistrar, birthCertificateDelivrancePlace, studentCode } = row;
          
          setValue('personId', id);
          setValue('firstName', firstName);
          setValue('lastName', lastName);
          setValue('birthDate', birthDate);
          setValue('birthPlace', birthPlace);
          setValue('particularSign', particularSign);
          setValue('gender', gender);
          setValue('civility', civility);
  
          setValue('birthCertificateDelivrancePlace', birthCertificateDelivrancePlace);
    
        setOpenPersonSearchFilter(false);
      }
    );

  // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<ITeacher>, Error, ITeacher>(
    _id > 0 ? updateTeacher : createTeacher, {
    onSuccess: (data: IResult<ITeacher>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      setCurrentFormNameAtom(`${t('Teacher')} - # ${data.data.id} # `);
      //setCurrentEntityIdForAction(data.data.id);

      //queryClient.invalidateQueries(['Teacher',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });

  //// Mutation for actions.....
  const { isLoading: isLoadingTeacherSubjet, isError: isErrorTeacherSubjet,
    isSuccess: isSuccessTeacherSubjet, error: errorTeacherSubjet,
    mutate: mutateSubjetTeacher } = useMutation<IResult<boolean>, Error, { id: number, schoolYearClassId: number, schoolYearClassSubjectId: number }>(
      setSubjectTeacher, {
      onSuccess: (data: IResult<boolean>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);

        //reset(data.data);
        setOpenSubjectTeacher(false);
        //queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {

        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenSubjectTeacher(false);
      }
    });


  const { data: _data, refetch } = useQuery<ITeacher>(['Teacher', _id], () => retrieveEntity('Teacher', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const [openSubjects, setOpenSubjects] = useState(false);

  const { data: teacherSubjects, refetch: refetchTeacherSubjects } =
    useQuery<{ id: number, schoolYearClassName: string, subjectLongName: string }[]>
      (['Teacher-Subjects', watchTeacherId, watchCurrentSchoolYearId],
        () => getTeacherSubjects(watchTeacherId, watchCurrentSchoolYearId),
        { refetchOnWindowFocus: false, enabled: watchTeacherId > 0 && watchCurrentSchoolYearId > 0 && openSubjects });

  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Teacher'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_TEACHER_STATUS, Enum_TEACHER_DIPLOMA, Enum_TEACHER_SPECIALIZATION, Enum_TEACHER_SERVICE, Enum_TEACHER_FUNCTION,
      Enum_EMPLOYEE_TYPE, Enum_DEFAULT_LANGUAGE, Enum_COUNTRY, Enum_PERSON_IDENTIFICATION,
      Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_MARIETAL_STATUS, Enum_ETHNICITY]));

  const { data: enumItems2, refetch: refetchEnumerationItems2 } = useQuery<IEnumerationItem[]>(['EnumerationItems-2', 'Teacher'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_CHRONIC_ILLNESS, Enum_DISABILITY_TYPE]));
  //const { data: schoolYears } = useQuery<ISchoolYear[]>(['SchoolYears'], () => getSchoolYears({ year: 0, name: '' }));

  type TeacherInfoView = 'absence' | 'discipline' | 'observation';
  const [teacherInfoView, setTeacherInfoView] = useState<TeacherInfoView>('absence');
  const handleChangeTeacherInfoView = (event: React.MouseEvent<HTMLElement>, newView: TeacherInfoView) => {

    if (newView === null)
      return;

    setTeacherInfoView(newView);
  }


  const handleClickOpenSubjects = (event: any) => {

    setOpenSubjects(true);
  }

  const [openMorePersonInfo, setOpenMorePersonInfo] = useState(false);

  type ParentType = 'father' | 'mother' | 'tutor';
  const [currentParentType, setCurrentParentType] = useState<ParentType>('father');
  const refParentType = useRef<ParentType>('father');
  const handleChangeCurrentParentType = (event: React.MouseEvent<HTMLElement>, newParentType: ParentType) => {
    if (isFalsy(newParentType))
      return;

    setCurrentParentType(newParentType);
    refParentType.current = newParentType;
  }

  type PersonType = 'teacher' | 'parent';
  const [personType, setCurrentPersonType] = useState<PersonType>('teacher');
  const refPersonType = useRef<PersonType>('teacher');

  const handleClickOpenPerson = (personType: PersonType) => (event: any) => {
    setCurrentPersonType(personType);
    refPersonType.current = personType;
    setOpenPersonFilter(true);
  }

  const handleClickRemovePerson = (personType: PersonType) => (event: any) => {
    if (personType === 'teacher') {
      setValue('personId', 0);
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('birthPlace', '');
      setValue('particularSign', '');
      setValue('civility', '');
      setValue('gender', '');
      // setValue('birthCertificateNumber', '');
      // setValue('civilRegistrar', '');
      setValue('birthCertificateDelivrancePlace', '');
      // setValue('studentCode', '');

      setValue('fatherId', 0);
      setValue('motherId', 0);
      setValue('tutorId', 0);

      setValue('fatherFirstName', '');
      setValue('fatherLastName', '');
      setValue('fatherBirthPlace', '');
      setValue('motherFirstName', '');
      setValue('motherLastName', '');
      setValue('motherBirthPlace', '');
      setValue('tutorFirstName', '');
      setValue('tutorLastName', '');
      setValue('tutorBirthPlace', '');
    } else {
      setValue(`${currentParentType}Id` as FieldPath<ITeacher>, 0);
      setValue(`${currentParentType}FirstName` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}LastName` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}BirthPlace` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}ParticularSign` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}Civility` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}Gender` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}BirthCertificateNumber` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}CivilRegistrar` as FieldPath<ITeacher>, '');
      setValue(`${currentParentType}birthCertificateDelivrancePlace` as FieldPath<ITeacher>, '');
    }
  }



  const handleClickOpenSchoolYearClass = (event: any) => {
    setOpenClassFilter(true);
  }



  function openFileDialog() {
    (document as any).getElementById("file-upload").click();
  }

  const setFile = (_event: any) => {
    let f = _event.target.files![0];
    var reader = new FileReader();

    reader.onload = function () {

      var binaryString = reader.result as string;

      const base64String = binaryString
        .replace('data:', '')
        .replace(/^.+,/, '');

      setValue("base64Picture", base64String);

    };

    reader.onerror = function () {
      console.log("File load failed");
    };

    reader.readAsDataURL(f);
  };

  const [teacherServiceFilter, setTeacherServiceFilter] = useState<string>('');

  const refEnumItems = useRef<IEnumerationItem[]>([]);
  useEffect(() => {
    refEnumItems.current = enumItems || [];

  }, [enumItems]);


  useEffect(() => {
    async function _() {

      if (isFalsy(watchTeacherId) || watchTeacherId <= 0) return;

      await loadTeacherSheets(watchTeacherId);
    }

    _();
  }, [watchTeacherId]);

  const loadTeacherSheets = async (teacherId: number) => {

    const { teacherAbsenceSheets, teacherDisciplinarySheets, teacherObservationSheets } = await getTeacherSheets(teacherId);

    setTeacherAbsenceSheets(teacherAbsenceSheets);
    setTeacherDisciplinarySheets(teacherDisciplinarySheets);
    setTeacherObservationSheets(teacherObservationSheets);
  }

  const debouncedNameChange = useRef(
    debounce(async () => {
      const { personId, firstName, lastName, particularSign } = getValues();

      if (personId > 0) return;

      if ((isFalsy(firstName) || firstName === '') && (isFalsy(lastName) || lastName === ''))
        return;

      const count = await getPersonsSearchCount(firstName, lastName, particularSign);

      if (!isFalsy(refPersonSnackbarId.current)) closeSnackbar(refPersonSnackbarId.current!);

      if (count > 0)
        refPersonSnackbarId.current = enqueueSnackbar(`${t('You have to check if person exists before save, verify it in this list of')} : ${count} ${t('_person')}(s)`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' }, persist: true, //autoHideDuration : 5000,
          action: xActionPersonSearch(firstName, lastName, particularSign)
        });

    }, 2500)
  ).current;


  const refPersonSnackbarId = useRef<SnackbarKey>();
  const xActionPersonSearch = (firstName: string, lastName: string, particularSign: string): SnackbarAction => (snackbarId: SnackbarKey) => (
    <>
      <Button onClick={() => {
        // setUrlPdf(`${globalConfig.get().apiUrl}/Download/${fileToken}/${fileName}`); 

        setOpenPersonSearchFilter(true);
        closeSnackbar(snackbarId);
      }}>
        <GrSearch size={24} />
      </Button>
      <Button onClick={() => { closeSnackbar(snackbarId) }}>
        <GrClose size={24} />
      </Button>
    </>
  )

  useEffect(() => {
    debouncedNameChange();
  }, [watchFirstName, watchLastName, watchParticularSign, debouncedNameChange]);

  useEffect(() => {
    setValue('civility', watchGender === 'M' ? 'Mr' : 'Mme');
  }, [watchGender]);


  useEffect(() => {
    setCurrentFormNameAtom(t('Teacher'));
    setCurrentBasicTextFilterProps(basicFilterTeacher);
  }, []);

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    if (_id > 0)
      retrieveData('Teacher', _id, refetch);
  }, [_id]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      setCurrentFormNameAtom(`${t('Teacher')} - # ${_data.id} #`);
      reset({
        ..._data, currentSchoolYearId: applicationSetup.currentSchoolYearId,
        currentSchoolYearClassId: 0,
        currentSchoolYearClassSubjectId: 0
      });
    }
  }, [_data]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    setCurrentFormNameAtom(t('Teacher'));
    reset(defaultTeacher);
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!checkEntitySaveAuthorization('Teacher', _id)) {
      setIsSaveLoading(false);
      return;
    }


    const data = getValues();
    if (data.firstName.trim() === '' && data.lastName.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    mutate(data);
  }

  const [selectedAbsenceIds, setSelectedAbsenceIds] = useState<number[]>([]);
  const [selectedDisciplineIds, setSelectedDisciplineIds] = useState<number[]>([]);
  const [selectedObservationIds, setSelectedObservationIds] = useState<number[]>([]);

  // const actionData = async (event: MouseEvent<HTMLButtonElement>) => {

  //   const arrDisciplinaries = selectedDisciplineIds.map(id => ({ entityName: 'TeacherDisciplinarySheet', entityId: id }));
  //   const arrAbsences = selectedAbsenceIds.map(id => ({ entityName: 'TeacherAbsenceSheet', entityId: id }));
  //   const arrObservations = selectedObservationIds.map(id => ({ entityName: 'TeacherObservationSheet', entityId: id }));

  //   setContextualItems([...arrDisciplinaries, ...arrAbsences, ...arrObservations]);
  //   openEntityActionDrawer('Teacher', _id, contextualItems);
  // }

  const [teacherDisciplinarySheets, setTeacherDisciplinarySheets] = useState<ITeacherSheet[]>([]);
  const [teacherAbsenceSheets, setTeacherAbsenceSheets] = useState<ITeacherSheet[]>([]);
  const [teacherObservationSheets, setTeacherObservationSheets] = useState<ITeacherSheet[]>([]);


  const [contextualItems, setContextualItems] = useState<IEntityNameId[]>([]);

  const handleTeacherSheetDoubleClick = (event: React.MouseEvent<unknown>, row: ITeacherSheet) => {

  }

  const handleTeacherSheetSelected = (event: React.MouseEvent<unknown>, row: ITeacherSheet) => {
    const { id } = row;
    const arr = contextualItems.filter(x => x.entityName !== 'TeacherSheet');
    //setContextualItems( [...arr, {entityName: 'RegistrationSheet', entityId: id}] );
    setContextualItems([{ entityName: 'TeacherSheet', entityId: id }]);
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('Teacher', _id);
  }

  const [openSubjectTeacher, setOpenSubjectTeacher] = useState<boolean>(false);
  const actionDataSetSubjectTeacher = async (event: MouseEvent<HTMLButtonElement>) => {
    const { status } = getValues();
    if (status !== '10') {
      enqueueSnackbar(t('This is possible on validated registration'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledSubjectTeacher(false);
    setOpenSubjectTeacher(true);
  }

  const [okButtonDisabledSubjectTeacher, setOkButtonDisabledSubjectTeacher] = useState<boolean>(false);
  const handleOkSubjectTeacher = async () => {
    const { id } = getValues();


    setOkButtonDisabledSubjectTeacher(true);
    mutateSubjetTeacher({
      id, schoolYearClassId: getValues().currentSchoolYearClassId,
      schoolYearClassSubjectId: getValues().currentSchoolYearClassSubjectId
    });
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={0.5} columnSpacing={0.1}>
          <Grid item xs={12} md={8} lg={7} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Personal information'))} `}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='gender' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(15% - 8px)' }} id="gender"
                      label={t('Gender')} inputProps={{ readOnly: false }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_PERSON_GENDER).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='civility' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(15% - 8px)' }} id="civility"
                      label={t('Civility')} inputProps={{ readOnly: true }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_PERSON_CIVILITY).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <TextField sx={{ width: 'calc(28% - 8px)' }} id="fn" label={lg.startsWith('fr') ? t('Last name') : t('First name')}
                  {...register(lg.startsWith('fr') ? 'lastName' : 'firstName')}

                  inputProps={{ readOnly: (watchPersonId > 0), style: { textTransform: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {(isFalsy(watchPersonId) || watchPersonId <= 0) ?
                          <IconButton color="primary" onClick={handleClickOpenPerson('teacher')}>
                            <ArrowDropDownCircleIcon />
                          </IconButton> :
                          <IconButton color="primary" onClick={handleClickRemovePerson('teacher')}>
                            <RemoveCircleIcon />
                          </IconButton>
                        }
                      </InputAdornment>
                    )
                  }}
                  helperText={(getValues().personId > 0 && roleEntities.find(re => re.entityName === 'Person')) ?
                    <FormHelperText>
                      <Link
                        href="#"
                        onClick={() => {
                          if (roleEntities.some(e => e.entityName === 'Person'))
                            navigate(`/person/${getValues().personId}`)
                        }}
                        sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                      >
                        {`${t('View details')} : ${getValues().personId}`}
                      </Link>
                    </FormHelperText> : null
                  } />
                <TextField sx={{ width: 'calc(27% - 8px)' }} id="ln" label={lg.startsWith('fr') ? t('First name') : t('Last name')}
                  {...register(lg.startsWith('fr') ? 'firstName' : 'lastName')}
                  inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(15% - 8px)' }} id="particularSign" label={t('Part. sign')}
                  {...register('particularSign')} 
                  inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }}/>
                {openPersonFilter && <FormDialog open={openPersonFilter} maxWidth='md'
                  okText='' cancelText='' title={t('Person')} onCancel={() => { }}
                  onClose={() => { setOpenPersonFilter(false); }} onOk={() => { setOpenPersonFilter(false); }}  >
                  <BasicTextFilterForm<IPerson> {...basicFilterPerson} />
                </FormDialog>}
                {openPersonSearchFilter && <FormDialog open={openPersonSearchFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Person')} onCancel={() => { }}
                                  onClose={() => { setOpenPersonSearchFilter(false); }} onOk={() => { setOpenPersonSearchFilter(false); }}  >
                                  <BasicTextFilterForm<IPerson> {...basicFilterPersonSearch} />
                                </FormDialog>}
              </Box>
              <Box sx={{ mt: 0.25, width: '100%', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }} >
                <Controller control={control}
                  name='birthDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Birth date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(17% - 8px)' }, disabled: (watchPersonId > 0)} }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <TextField sx={{ width: 'calc(25% - 8px)' }} id="birthPlace" label={t('Birth place')}
                  {...register('birthPlace')} 
                  inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <Controller name='marietalStatus' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(20% - 8px)' }} id="marietalStatus"
                      label={t('Marietal status')} inputProps={{ readOnly: (watchPersonId > 0) }}  >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_MARIETAL_STATUS).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller
                  render={({ field: { onChange, onBlur, name, value, ref } }) => {
                    return (
                      <NumberFormat
                        label={`# ${t('Child')}(s)`} sx={{ width: 'calc(10% - 8px)' }}
                        disabled={watchPersonId > 0}
                        allowEmptyFormatting={false}
                        control={control}
                        thousandSeparator={true}
                        decimalScale={2}
                        onValueChange={(v) => onChange(v.floatValue)}
                        defaultValue={value}
                        value={value}
                        customInput={TextFieldRight}
                      />
                    );
                  }}
                  name={`numberOfDependents`}
                  control={control}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', width: 'auto', flexGrow: 1 }}>
                  <Controller name='ethnicityCode' control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField select onChange={onChange} value={value}
                        fullWidth
                        id="ethnicityCode"
                        label={t('Ethnicity')} inputProps={{ readOnly: (watchPersonId > 0) }}>
                        {enumItems && enumItems.filter(e =>
                          e.enumerationCode === Enum_ETHNICITY).map(
                            (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                        }
                      </TextField>
                    )}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenMorePersonInfo(true)}
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  >
                    {t('Details')}
                  </Button>
                </Box>

              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable1" label={`${t('Portable')} 1`}
                  {...register('portable1')} inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable2" label={`${t('Portable')} 2`}
                  {...register('portable2')} inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="email1" label={`${t('Email')} 1`}
                  {...register('email1')} inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="email2" label={`${t('Email')} 2`}
                  {...register('email2')} inputProps={{ readOnly: (watchPersonId > 0), autoComplete: 'new-password', style: { textTransform: 'none' } }} />
              </Box>
              {openMorePersonInfo && <FormDialog open={openMorePersonInfo} maxWidth='md'
                okText={t('Close')} cancelText='' title={`${t('More infos')} - ${getValues('firstName')} ${getValues('lastName')} `} onCancel={() => { }}
                onClose={() => { setOpenMorePersonInfo(false); }} onOk={() => { setOpenMorePersonInfo(false); }}  >
                <Stack flexDirection='column'  >
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <TextField sx={{ width: 'calc(25% - 8px)' }} id="particularSign" label={t('Part. sign')}
                      {...register('particularSign')} />
                    <TextField sx={{ width: 'calc(25% - 8px)' }} id="birthCertificateDelivrancePlace"
                      label={t('Civil state center')}
                      {...register('birthCertificateDelivrancePlace')} />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                      {`${t('Biometric informations and others')}`}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Controller name='bloodType' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="defaultLanguage"
                          label={t('Blood type')} inputProps={{ readOnly: false }}  >
                          <MenuItem value='O'>O</MenuItem>
                          <MenuItem value='A'>A</MenuItem>
                          <MenuItem value='B'>B</MenuItem>
                          <MenuItem value='AB'>AB</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller name='rhesusFactor' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="defaultLanguage"
                          label={t('Rhesus factor')} inputProps={{ readOnly: false }}  >
                          <MenuItem value='+'>+</MenuItem>
                          <MenuItem value='-'>-</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller name='chronicIllnessCode' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="chronicIllnessCode"
                          label={t('Chronic illness')} inputProps={{ readOnly: false }}>
                          {enumItems2 && enumItems2.filter(e =>
                            e.enumerationCode === Enum_CHRONIC_ILLNESS).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <FormControlLabel sx={{ width: 'calc(40% - 8px)' }}
                      label={`${t('Has specific needs ?')}`}
                      control={
                        <Controller
                          name='hasSpecificNeeds'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                        />} />
                    <Controller name='specificNeedPurposeCode' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(60% - 8px)' }} id="specificNeedPurposeCode"
                          label={t('Purpose')} inputProps={{ readOnly: !watchHasSpecificNeeds }} >
                          {enumItems2 && enumItems2.filter(e =>
                            e.enumerationCode === Enum_SPECIFIC_NEED_PURPOSE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <FormControlLabel sx={{ width: 'calc(40% - 8px)' }}
                      label={`${t('Has disability ?')}`}
                      control={
                        <Controller
                          name='hasDisability'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                        />} />
                    <Controller name='disabilityType' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(60% - 8px)' }} id="disabilityType"
                          label={t('Disability')} inputProps={{ readOnly: !watchHasDisability }} >
                          {enumItems2 && enumItems2.filter(e =>
                            e.enumerationCode === Enum_DISABILITY_TYPE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <ToggleButtonGroup size="small"
                      color="primary"
                      value={currentParentType}
                      exclusive
                      onChange={(event, parentType) => handleChangeCurrentParentType(event, parentType)}
                      aria-label="Small sizes" fullWidth
                    >
                      {
                        [{ value: 'father', name: t('_Father') },
                        { value: 'mother', name: t('_Mother') },
                        { value: 'tutor', name: t('_Tutor') }
                        ].map(x =>
                          <ToggleButton value={x.value} key={x.value}>
                            {x.name}
                          </ToggleButton>
                        )
                      }
                    </ToggleButtonGroup>

                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} key={` person ${currentParentType} name`}>
                    <Controller
                      name={`${currentParentType}Gender`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          select
                          label={t('Gender')}
                          sx={{ width: '15%' }}
                          {...field}
                        >
                          {(enumItems || [])
                            .filter(e => e.enumerationCode === Enum_PERSON_GENDER)
                            .map(e => (
                              <MenuItem key={e.code} value={e.code}>
                                {e.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      name={`${currentParentType}Civility`}
                      control={control}
                      render={({ field }) => (
                        <TextField key={`${currentParentType} civility`}
                          select
                          label={t('Civility')}
                          sx={{ width: '15%' }}
                          inputProps={{ readOnly: true }}
                          {...field}
                        >
                          {(enumItems || [])
                            .filter(e => e.enumerationCode === Enum_PERSON_CIVILITY)
                            .map(e => (
                              <MenuItem key={e.code} value={e.code}>
                                {e.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="fn" key={`${currentParentType} ln`}
                      label={lg.startsWith('fr') ? t('Last name') : t('First name')}
                      {...register(`${currentParentType}${lg.startsWith('fr') ? 'LastName' : 'FirstName'}`)}
                      inputProps={{
                        readOnly: ((getValues(`${currentParentType}Id` as any) as number) > 0),
                        style: { textTransform: 'none' }
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            {(isFalsy(getValues(`${currentParentType}Id` as any)) ||
                              (getValues(`${currentParentType}Id` as any) as number) <= 0) ?
                              <IconButton color="primary" onClick={handleClickOpenPerson('parent')}>
                                <ArrowDropDownCircleIcon />
                              </IconButton> :
                              <IconButton color="primary" onClick={handleClickRemovePerson('parent')}>
                                <RemoveCircleIcon />
                              </IconButton>
                            }
                          </InputAdornment>
                        )
                      }}
                      helperText={(getValues(`${currentParentType}Id` as any) as number) > 0 ?
                        <FormHelperText>{(getValues(`${currentParentType}Id` as any) as number)}</FormHelperText> : null
                      } />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="ln" key={`${currentParentType} fn`}
                      label={lg.startsWith('fr') ? t('First name') : t('Last name')}
                      {...register(`${currentParentType}${lg.startsWith('fr') ? 'FirstName' : 'LastName'}`)}
                      inputProps={{
                        readOnly: ((getValues(`${currentParentType}Id` as any) as number) > 0),
                        style: { textTransform: 'none' }
                      }} />
                  </Box>
                  <Box key={` person ${currentParentType} birth date`}
                    sx={{ mt: 0.25, width: '100%', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <Controller
                      name={`${currentParentType}BirthDate`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          label={t('Birth date')}
                          readOnly={(getValues(`${currentParentType}Id` as any) as number) > 0}
                          onChange={onChange}
                          value={new Date(value)}
                          slotProps={{ textField: { sx: { width: '20%' } } }}
                        />
                      )}
                    />
                    <TextField
                      sx={{ width: 'calc(25% - 8px)' }}
                      id="birthPlace"
                      label={t('Birth place')}
                      {...register(`${currentParentType}BirthPlace`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />
                    <TextField
                      sx={{ width: 'calc(20% - 8px)' }}
                      id="particularSign"
                      label={t('Particular sign')}
                      {...register(`${currentParentType}ParticularSign`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />

                    <TextField
                      sx={{ width: 'calc(15% - 8px)' }}
                      id="birthCertificateNumber"
                      label={t('Birth certif. N°')}
                      {...register(`${currentParentType}BirthCertificateNumber`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />
                    <TextField
                      sx={{ width: 'calc(20% - 8px)' }}
                      id="civilRegistrar"
                      label={t('Civil registar')}
                      {...register(`${currentParentType}CivilRegistrar`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />

                  </Box>

                  <Box sx={{ mt: 1, width: '100%' }} key={` person ${currentParentType} contact`}>
                    <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable1" label={`${t('Portable')} 1`}
                      {...register(`${currentParentType}Portable1`)}
                      inputProps={{
                        readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0,
                        autoComplete: 'new-password', style: { textTransform: 'none' }
                      }} />
                    <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable2" label={`${t('Portable')} 2`}
                      {...register(`${currentParentType}Portable2`)}
                      inputProps={{
                        readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0,
                        autoComplete: 'new-password', style: { textTransform: 'none' }
                      }} />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="email1" label={`${t('Email')} 1`}
                      {...register(`${currentParentType}Email1`)}
                      inputProps={{
                        readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0,
                        autoComplete: 'new-password', style: { textTransform: 'none' }
                      }} />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="email2" label={`${t('Email')} 2`}
                      {...register(`${currentParentType}Email2`)}
                      inputProps={{
                        readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0,
                        autoComplete: 'new-password', style: { textTransform: 'none' }
                      }} />
                  </Box>

                </Stack>
              </FormDialog>}
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Employment and qualifications'))}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 0.5, width: '100%', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnActionSetSubjectTeacher' onClick={actionDataSetSubjectTeacher} sx={{ display: 'none' }} />

                <TextField sx={{ width: 'calc(20% - 8px)' }} id="id" label={t('Regis. Number')}
                  {...register('registrationNumber')} />
                <Controller control={control}
                  name='hiringDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Hiring date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(17% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                    />)}
                />
                <Controller name='status' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(20% - 8px)' }} id="status"
                      label={t('Status')} inputProps={{ readOnly: false }} >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_TEACHER_STATUS).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller control={control}
                  name='statusDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Status date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(17% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                    />)}
                />
                <Controller name='employeeType' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="employeeType"
                      label={t('Empl. type')} inputProps={{ readOnly: false }}>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_EMPLOYEE_TYPE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 0.5, width: '100%', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Controller name='lastDiploma' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(20% - 8px)' }} id="lastDiploma"
                      label={t('Last diploma')} inputProps={{ readOnly: false }} >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_TEACHER_DIPLOMA).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller control={control}
                  name='diplomaDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Diploma date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(17% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                    />)}
                />
                <Controller name='specialization' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(20% - 8px)' }} id="specialization"
                      label={t('Specialization')} inputProps={{ readOnly: false }} >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_TEACHER_SPECIALIZATION).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='functionCode' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(17% - 8px)' }} id="functionCode"
                      label={t('Function')} inputProps={{ readOnly: false }} >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_TEACHER_FUNCTION).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller
                  name="serviceCode"
                  control={control}
                  render={({ field }) => {
                    const options =
                      enumItems?.filter((e) => e.enumerationCode === Enum_TEACHER_SERVICE).map((e) => e.name) || [];

                    return (
                      <Autocomplete
                        {...field}
                        options={options}
                        onChange={(_, value) => field.onChange(value)}
                        value={field.value || ''}
                        disableClearable
                        sx={{
                          width: 'calc(25% - 8px)',      // same width as other inputs
                          '& .MuiInput-root': {
                            marginTop: '8px', // 🔥 adjust margin-top to align with other inputs
                          },
                          '& .MuiInput-input': {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },

                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${t('Service')} / ${t('Department')}`}
                            variant="standard"
                            InputLabelProps={{ shrink: true, sx: { fontWeight: 'bold', mt: 0 } }}
                            sx={{
                              '& .MuiInput-input': {
                                paddingTop: 0,
                                paddingBottom: 0,
                              },
                            }}
                          />
                        )}
                      />
                    );
                  }}
                />
                {/* <FormControlLabel sx={{ width: 'calc(20% - 8px)' }}
                  label={t('Teacher ?')}
                  control={
                    <Controller
                      name='hasSubject'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                <FormControlLabel sx={{ width: 'calc(15% - 8px)' }}
                  label={t('Driver ?')}
                  control={
                    <Controller
                      name='isDriver'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} /> */}

              </Box>

              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Teaching information'))} `}
                </Typography>
              </Box>
              <Box sx={{
                mt: 1,
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }} >
                <Controller
                  render={({ field: { onChange, onBlur, name, value, ref } }) => {
                    return (
                      <NumberFormat
                        label={`# ${t('Weekly hours')}`} sx={{ width: 'calc(15% - 8px)' }} disabled={true}
                        allowEmptyFormatting={false}
                        control={control}
                        thousandSeparator={true}
                        decimalScale={2}
                        onValueChange={(v) => onChange(v.floatValue)}
                        defaultValue={value}
                        value={value}
                        customInput={TextFieldRight}
                      />
                    );
                  }}
                  name={`weeklyHours`}
                  control={control}
                />
                <Controller name='schedulePreference' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="schedulePreference"
                      label={`${t('Time slot')} - Preference`} inputProps={{ readOnly: false }}  >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_MARIETAL_STATUS).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', width: 'auto', flexGrow: 1 }}>
                  <TextField fullWidth id="pedagocicalNotes" label={`${t('Pedagogical notes')}`}
                    {...register('pedagogicalNotes')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />

                  <Button
                    variant="outlined"
                    onClick={() => setOpenSubjects(true)}
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  >
                    {t('Subjects')}
                  </Button>
                </Box>
              </Box>
              {openSubjects && <FormDialog open={openSubjects} maxWidth='sm' height='65vh'
                okText={t('Close')} cancelText='' title={`${t('More infos')} - ${getValues('firstName')} ${getValues('lastName')} `} onCancel={() => { }}
                onClose={() => { setOpenSubjects(false); }} onOk={() => { setOpenSubjects(false); }}  >
                <Stack flexDirection='column'  >

                  <Box key={`${getValues().currentSchoolYearId} -${getValues().id}`}
                    sx={{ display: 'flex', alignItems: 'center', width: 'auto', flexGrow: 1 }}>
                    <Controller name='currentSchoolYearId' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value}
                          fullWidth
                          id="currentSchoolYearId"
                          label={t('School year')} inputProps={{ readOnly: false }}>
                          {schoolYears && schoolYears.map(
                            (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <EnhancedTable<{ id: number, schoolYearClassName: string, subjectLongName: string }> rows={teacherSubjects || []}
                      headCells={[

                        { id: 'schoolYearClassName', label: t('Type'), display: true, type: 'string', },
                        { id: 'subjectLongName', label: t('Summary'), display: true, type: 'string', },

                      ]}
                      title={`${t('Class')} -/- ${t('Subject')}`} objKey='id'
                      stateSelected={undefined}
                      onRowSelected={handleTeacherSheetSelected} rowCheckedMode='single'
                      onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                      onRowDoubleClick={undefined}
                      rowActionIcon={undefined}
                      toolbarActions={[
                        // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                      ]}
                    />
                  </Box>
                </Stack>
              </FormDialog>}
              {openSubjectTeacher && <FormDialog open={openSubjectTeacher} maxWidth='sm' height='35vh'
                okText={t('OK')} cancelText={t('Cancel')} title={`${t('Subject')} ...`}
                okButtonDisabled={okButtonDisabledSubjectTeacher}
                onCancel={() => { setOpenSubjectTeacher(false); }}
                onClose={() => { setOpenSubjectTeacher(false); }} onOk={handleOkSubjectTeacher}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <TextField sx={{ width: 'calc(100% - 8px)' }} id="currentSchoolYearClassName"
                      label={t('Class')} {...register('currentSchoolYearClassName')} inputProps={{ readOnly: true }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton color="primary" onClick={handleClickOpenSchoolYearClass}>
                              <ArrowDropDownCircleIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }} />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }}
                    key={`${getValues().currentSchoolYearClassId} ${schoolYearClassSubjects.map(x => x.id).join('-')}`}>
                    <Controller name='currentSchoolYearClassSubjectId' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value}
                          fullWidth
                          id="currentSchoolYearClassSubjectId"
                          label={t('Subject')} inputProps={{ readOnly: false }}>
                          {schoolYearClassSubjects && schoolYearClassSubjects.map(
                            (x, idx) => <MenuItem key={x.id} value={x.id}>{x.schoolYearTemplateSubjectLongName}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                </Stack>
              </FormDialog>}
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Administrative information'))} `}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller control={control}
                  name='serviceStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Service start date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <TextField sx={{ width: 'calc(30% - 8px)' }} id="pedagogicalNotes" label={`${t('Assignment decree')}`}
                  {...register('assignmentDecree')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <Controller control={control}
                  name='positionStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Position start date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <TextField sx={{ width: 'calc(30% - 8px)' }} id="nominationDecree" label={`${t('Nomination decree')}`}
                  {...register('nominationDecree')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller control={control}
                  name='firstServiceStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('First service date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <Controller control={control}
                  name='firstPositionStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('First position date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <Controller control={control}
                  name='retirementDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Retirement date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <TextField sx={{ width: 'calc(40% - 8px)' }} id="previousInstitution" label={`${t('Previos institution')}`}
                  {...register('previousInstitution')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} lg={5} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'>
              <Box sx={{ mt: 1, width: '100%' }} >
                <ToggleButtonGroup size="small"
                  color="primary"
                  value={teacherInfoView}
                  exclusive
                  onChange={(event, newView) => handleChangeTeacherInfoView(event, newView)}
                  aria-label="Small sizes" fullWidth
                >
                  <ToggleButton value="absence">{t('Absences')}</ToggleButton>
                  <ToggleButton value="discipline">{t('Disciplines')} </ToggleButton>
                  <ToggleButton value="observation">{t('Observations')} </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {teacherInfoView === 'discipline' && <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<ITeacherSheet> rows={teacherDisciplinarySheets}
                  headCells={[
                    { id: 'id', label: t('Id'), display: false, type: 'numeric', },
                    { id: 'disciplinaryType', label: t('Type'), display: true, type: 'string', },
                    { id: 'disciplinaryEventSummary', label: t('Summary'), display: true, type: 'string', },

                    { id: 'isCancelled', label: t('Cancelled ?'), display: true, type: 'boolean', },
                    //{id:'cancellationPurpose', label : t('Date'),  display: true, type: 'string', },

                  ]}
                  title={t(`Disciplinaries`)} objKey='id'
                  stateSelected={[selectedDisciplineIds, setSelectedDisciplineIds]}
                  onRowSelected={handleTeacherSheetSelected} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                  onRowDoubleClick={handleTeacherSheetDoubleClick}
                  rowActionIcon={undefined}
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />
              </Box>}
              {teacherInfoView === 'absence' && <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<ITeacherSheet> rows={teacherAbsenceSheets}
                  headCells={[
                    { id: 'id', label: t('Id'), display: false, type: 'numeric', },
                    { id: 'absenceType', label: t('Type'), display: true, type: 'string', },
                    { id: 'absencePurpose', label: t('Purpose'), display: true, type: 'string', },

                    { id: 'justifedAbsence', label: t('Justified ?'), display: true, type: 'boolean', },
                    { id: 'isCancelled', label: t('Cancelled ?'), display: true, type: 'boolean', },
                    //{id:'cancellationPurpose', label : t('Date'),  display: true, type: 'string', },

                  ]}
                  title={`${t('_Leave_')}(s)`} objKey='id'
                  stateSelected={[selectedAbsenceIds, setSelectedAbsenceIds]}
                  onRowSelected={handleTeacherSheetSelected} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                  onRowDoubleClick={handleTeacherSheetDoubleClick}
                  rowActionIcon={undefined}
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />

              </Box>}
              {teacherInfoView === 'observation' && <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<ITeacherSheet> rows={teacherObservationSheets}
                  headCells={[
                    { id: 'id', label: t('Id'), display: false, type: 'numeric', },
                    { id: 'observationPurpose', label: t('Purpose'), display: true, type: 'string', },
                    { id: 'observationDescription', label: t('Description'), display: true, type: 'string', },
                    { id: 'observationDate', label: t('Date'), display: true, type: 'date', },

                    { id: 'isCancelled', label: t('Cancelled ?'), display: true, type: 'boolean', },
                    //{id:'cancellationPurpose', label : t('Date'),  display: true, type: 'string', },                                
                  ]}
                  title={t(`Observations`)} objKey='id'
                  stateSelected={[selectedObservationIds, setSelectedObservationIds]}
                  onRowSelected={handleTeacherSheetSelected} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                  onRowDoubleClick={handleTeacherSheetDoubleClick}
                  rowActionIcon={undefined}
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />
              </Box>}

              {displayEnumerationItemDialog && <DialogEnumerationItemForm
                {...{
                  open: displayEnumerationItemDialog,
                  title: '',
                  enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                  afterSave: async () => { await refetchEnumerationItems() }
                }}
              />
              }
              {openClassFilter && <FormDialog open={openClassFilter} maxWidth='md'
                okText={''} cancelText={t('Cancel')} title={t('Classes')} onCancel={() => { setOpenClassFilter(false); }}
                onClose={() => { setOpenClassFilter(false); }} onOk={() => { }}  >
                <BasicTextFilterForm<ISchoolYearClass> {...basicFilterSchoolYearClass} />
              </FormDialog>}
            </Stack>
          </Grid>

        </Grid>
      </Box>
    </FormProvider>
  )
}

